/**
 * Snow and ice: the winter ground cover that replaces every painted material at once.
 *
 * ## Why this is a cover and not a palette material
 *
 * Snow is not a thing you paint a bed with — it is a thing that happens *to* the whole site, and the
 * beds, paths and pond are all still under it. Adding it as a {@link SurfaceLayer} would mean
 * painting every texel of the map on the way into winter and unpainting them on the way out, which
 * would destroy the design to show a season. So the cover lives outside the paint entirely: the
 * ground shader keeps blending the site's real materials and then the cover is laid over the result,
 * and switching it off restores the garden exactly because the garden was never touched.
 *
 * Ice is deliberately *translucent* rather than opaque. A glaze that hid the site would be an
 * accurate picture of a frozen pond and a useless drawing — you could no longer see where the paving
 * ends and the lawn begins. The frozen surface tints and darkens what it froze over, and only the
 * fracture lines, the trapped air and the rime read as white. That keeps the plan legible under it
 * and is also what real ice looks like from standing height.
 *
 * ## Why the swatches are drawn here rather than shipped as files
 *
 * The same argument as `sites/swatchforge.mjs`: an arithmetic swatch costs no bytes in the repo, is
 * tweakable by changing a number rather than by re-exporting an image, and cannot go stale against
 * the code that samples it. It also means a variant costs a function, which is what makes offering
 * three kinds of snow reasonable — fresh powder, settled snow and trodden snow are three genuinely
 * different pictures of a garden, not three presets of one.
 *
 * Drawn at {@link WINTER_TEXELS} rather than the palette's 512. A snow tile covers 3 m of ground, so
 * 256 is a texel per 12 mm — finer than any feature snow actually has — where a paving swatch has a
 * 14 mm joint to keep sharp and genuinely needs 512. Halving the side quarters the work, and the
 * work happens on the main thread the moment the designer ticks the box.
 */

import type { GroundCoverState } from '@/environment/EnvironmentState';
import { clamp01, fbm, fract, hash2, mix, noise, smooth01, wrap } from './SwatchNoise';

/** Side length of a winter swatch. See the module note on why it is not the palette's 512. */
export const WINTER_TEXELS = 256;

export type WinterCoverKind = 'snow' | 'ice';

export type SnowSwatchId = 'fresh' | 'settled' | 'trodden';
export type IceSwatchId = 'black' | 'cracked' | 'rime';

/** RGB plus a fourth channel whose meaning depends on the kind — see {@link WinterSwatch.paint}. */
type PaintFn = (u: number, v: number, out: number[]) => void;

/**
 * Generic in its own id so a variant picker can be typed: `WinterSwatch<SnowSwatchId>[]` hands the
 * panel a callback that takes exactly the ids that row can produce, and `setGroundCover` accepts it
 * without a cast. Defaulted to `string` for everything that only wants to read a swatch.
 */
export interface WinterSwatch<Id extends string = string> {
  id: Id;
  kind: WinterCoverKind;
  /** Panel label. */
  name: string;
  /** One line under the chips, saying what this is a picture of. */
  hint: string;
  /** Average colour, for the chip. */
  color: string;
  /** Roughness the ground takes where this cover is at full strength. Clear ice is the shiny one. */
  roughness: number;
  /** Metres one tile of the swatch covers on the ground. */
  tileMeters: number;
  /**
   * Metres one tile of the *drift mask* covers. The mask is the swatch's own fourth channel sampled
   * at this much coarser scale, and it is only read when snow lies over ice — it is what makes the
   * two covers together read as wind-scoured patches metres across rather than as a flat average.
   */
  driftMeters: number;
  /** What this is, in the language a render prompt wants. The only description an image model sees. */
  character: string;
  /**
   * Draw one texel. `out` is `[r, g, b, a]` in 0–1.
   *
   * For snow the fourth channel is **drift depth**: how deep the cover lies here, read as the patch
   * mask above. For ice it is **opacity**: how much of the ice's own colour replaces what is under
   * it, so clear ice writes a low value everywhere except along its fractures.
   */
  paint: PaintFn;
}

/* ---------------------------------- Shading helpers ---------------------------------- */

/** One texel, in UV. The finite difference below is taken at this width. */
const TEXEL = 1 / WINTER_TEXELS;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const rgb = (r: number, g: number, b: number): Rgb => ({ r, g, b });

const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb =>
  rgb(mix(a.r, b.r, t), mix(a.g, b.g, t), mix(a.b, b.b, t));

/** Write a colour plus a flat offset into a paint function's output. */
function writeRgb(out: number[], colour: Rgb, lift = 0): void {
  out[0] = clamp01(colour.r + lift);
  out[1] = clamp01(colour.g + lift);
  out[2] = clamp01(colour.b + lift);
}

/**
 * Lambert-ish shading of a height field, lit from the upper left.
 *
 * Four extra taps of `height` per texel, which is the whole cost of making snow look like snow: a
 * field of noise used as *colour* reads as static, and the same field used as *relief* reads as a
 * surface. Returns a multiplier around 1.
 */
function relief(height: (u: number, v: number) => number, u: number, v: number, strength: number): number {
  const dx = height(u + TEXEL, v) - height(u - TEXEL, v);
  const dy = height(u, v + TEXEL) - height(u, v - TEXEL);
  return 1 + (dx * 0.7 + dy * 0.7) * strength;
}

/**
 * Worley F2 − F1 on a wrapped lattice: near zero along the boundary between two cells, rising away
 * from it. A fracture network for the price of nine hashes, and tileable because the cell indices
 * wrap.
 */
function worleyEdge(u: number, v: number, cells: number, seed: number): number {
  const gx = u * cells;
  const gy = v * cells;
  const bx = Math.floor(gx);
  const by = Math.floor(gy);
  let first = Infinity;
  let second = Infinity;
  for (let oy = -1; oy <= 1; oy += 1) {
    for (let ox = -1; ox <= 1; ox += 1) {
      const wx = wrap(bx + ox, cells);
      const wy = wrap(by + oy, cells);
      const sx = bx + ox + hash2(wx, wy, seed);
      const sy = by + oy + hash2(wx, wy, seed + 17);
      const d = Math.hypot(gx - sx, gy - sy);
      if (d < first) {
        second = first;
        first = d;
      } else if (d < second) {
        second = d;
      }
    }
  }
  return second - first;
}

/* -------------------------------------- Snow -------------------------------------- */

/**
 * Fresh powder, undisturbed.
 *
 * Almost white, and almost featureless — which is the trap. Snow with no relief renders as a flat
 * card, so what carries it is the shadow: the hollows go blue (they see sky, not sun) while the
 * crests stay white, and the whole difference is a few percent of value.
 */
const freshPowder: PaintFn = (u, v, out) => {
  const height = (x: number, y: number): number => fbm(x, y, 7, 3, 211) * 0.72 + fbm(x, y, 26, 2, 223) * 0.28;
  const h = height(u, v);
  const lit = clamp01(relief(height, u, v, 9) * 0.5 + h * 0.5 + 0.25);

  // Blue in the hollows, warm white on the crests. Snow is one of the few materials whose shadow is
  // a different hue rather than a darker version of the same one.
  const shadow = rgb(0.788, 0.833, 0.925);
  const crest = rgb(0.985, 0.988, 0.996);
  // Very sparse, single-texel glints. Any denser and it reads as sensor noise.
  const glint = noise(u, v, 128, 128, 227) > 0.982 ? 0.05 : 0;

  writeRgb(out, mixRgb(shadow, crest, lit), glint);
  out[3] = smooth01((fbm(u, v, 3, 3, 229) - 0.3) / 0.42);
};

/**
 * Settled snow, a day or two old and wind-worked.
 *
 * Denser and a shade greyer than powder, with sastrugi — the shallow ridges wind cuts across a field
 * — from a noise stretched hard along one axis.
 */
const settledSnow: PaintFn = (u, v, out) => {
  // The ridges are sheared by a whole period across the tile — so they run diagonally and still
  // wrap — and warped by a low-frequency field. Both matter: an unsheared stretched noise reads as
  // brushed metal, and an unwarped one reads as corduroy. Wind does neither.
  const height = (x: number, y: number): number => {
    const warp = (fbm(x, y, 4, 2, 331) - 0.5) * 0.05;
    const ridges = noise(fract(x + warp + y), y + warp, 9, 20, 311);
    return fbm(x, y, 5, 3, 307) * 0.5 + ridges * 0.32 + fbm(x, y, 20, 2, 313) * 0.18;
  };
  const h = height(u, v);
  const lit = clamp01(relief(height, u, v, 11) * 0.5 + h * 0.45 + 0.28);

  const shadow = rgb(0.729, 0.776, 0.859);
  const crest = rgb(0.949, 0.961, 0.973);
  // A crust catches the light along the top of each ridge; below the threshold nothing happens.
  const crust = smooth01((h - 0.66) / 0.22) * 0.03;

  writeRgb(out, mixRgb(shadow, crest, lit), crust);
  out[3] = smooth01((fbm(u, v, 3, 3, 317) - 0.26) / 0.4);
};

/**
 * Trodden snow: walked on, shovelled to the edges, going grey.
 *
 * The one that makes a garden look *lived in* in January. Footfall-scale lumps from a jittered cell
 * lattice, grit and leaf litter worked into it, and enough of the ground's grey coming through that
 * it never reads as a fresh fall.
 */
const troddenSnow: PaintFn = (u, v, out) => {
  const cells = 9; // ~28 cm at a 2.5 m tile — one boot
  const height = (x: number, y: number): number => {
    const gx = x * cells;
    const gy = y * cells;
    const bx = Math.floor(gx);
    const by = Math.floor(gy);
    // Prints *accumulate* rather than compete. Taking the deepest of the overlapping ones is the
    // obvious reading and it is what made the first two versions of this look like a hide: every
    // print keeps its own rim, so the field is a scatter of discs. Summing them merges the places
    // that have been crossed twice into trampled ground and leaves a single crossing shallow, which
    // is what a path through snow actually is.
    let trodden = 0;
    for (let oy = -1; oy <= 1; oy += 1) {
      for (let ox = -1; ox <= 1; ox += 1) {
        const wx = wrap(bx + ox, cells);
        const wy = wrap(by + oy, cells);
        if (hash2(wx, wy, 401) > 0.9) continue;
        const sx = bx + ox + hash2(wx, wy, 409);
        const sy = by + oy + hash2(wx, wy, 419);
        const d = Math.hypot(gx - sx, gy - sy) / (0.6 + hash2(wx, wy, 421) * 0.55);
        trodden += (1 - smooth01(d)) * (0.35 + hash2(wx, wy, 423) * 0.65);
      }
    }
    return (1 - clamp01(trodden * 0.55)) * 0.7 + fbm(x, y, 10, 3, 431) * 0.3;
  };
  const h = height(u, v);
  const lit = clamp01(relief(height, u, v, 10) * 0.5 + h * 0.5 + 0.16);

  const shadow = rgb(0.639, 0.663, 0.706);
  const crest = rgb(0.886, 0.898, 0.906);
  // Grit and leaf litter, worked into the hollows where the snow has been walked down rather than
  // scattered evenly — evenly is what turns a dirty snowfield into terrazzo.
  const dirt = smooth01((fbm(u, v, 9, 3, 433) - 0.56) / 0.3) * (1 - h) * 0.42;
  const grime = rgb(0.322, 0.286, 0.243);

  writeRgb(out, mixRgb(mixRgb(shadow, crest, lit), grime, dirt));
  // Shallower and patchier than a fresh fall: this is snow that has been pushed about.
  out[3] = smooth01((fbm(u, v, 4, 3, 439) - 0.34) / 0.36) * 0.86;
};

/* --------------------------------------- Ice --------------------------------------- */

/**
 * Black ice: a hard freeze straight onto the ground, with no air in it.
 *
 * Nearly all of what you see is the site underneath, tinted and darkened by the shader. What this
 * swatch contributes is the *structure* — the pale pressure ridges where the sheet has heaved, and
 * the faint bloom of frost that catches on any surface at all.
 */
const blackIce: PaintFn = (u, v, out) => {
  // Sparse *and* narrow, unlike the plate ice below. Two cells over a 4 m tile puts the ridges
  // metres apart, and the divisor is what keeps each one a line rather than a band: `worleyEdge`
  // returns its distance in cells, so dividing by 0.3 at this cell size lit up two thirds of the
  // ground and rendered the whole garden as reptile skin. 0.05 of a 2 m cell is a 10 cm ridge.
  const ridge = 1 - smooth01(worleyEdge(u, v, 2, 503) / 0.05);
  const bloom = smooth01((fbm(u, v, 9, 3, 509) - 0.56) / 0.32);
  const grain = (fbm(u, v, 40, 2, 521) - 0.5) * 0.05;

  writeRgb(out, rgb(0.792, 0.855, 0.886), grain);
  // Low everywhere: this is a window, not a coat of paint. The ridges and the frost are the only
  // places the ice's own colour wins, and even they never close it up.
  out[3] = clamp01(0.05 + ridge * 0.4 + bloom * 0.18);
};

/**
 * Plate ice: frozen, thawed and refrozen, so the sheet is a mosaic of plates with white fractures
 * between them and air trapped in the body.
 *
 * The most legible of the three at a distance, because the fracture network gives the eye something
 * to read as a surface where clear ice gives it only the ground below.
 */
const crackedIce: PaintFn = (u, v, out) => {
  const crack = 1 - smooth01(worleyEdge(u, v, 7, 601) / 0.13);
  const hairline = (1 - smooth01(worleyEdge(u, v, 15, 607) / 0.05)) * 0.55;
  // Trapped air: sparse pale discs, thickest near the fractures where the sheet worked most.
  const bubbles = smooth01((fbm(u, v, 26, 2, 613) - 0.66) / 0.16);
  const grain = (fbm(u, v, 44, 2, 617) - 0.5) * 0.06;

  const white = rgb(0.906, 0.937, 0.949);
  const body = rgb(0.733, 0.792, 0.823);
  const face = Math.max(crack, hairline);
  writeRgb(out, mixRgb(body, white, face), grain);
  out[3] = clamp01(0.16 + face * 0.72 + bubbles * 0.3);
};

/**
 * Rime: an opaque milky freeze, the way a puddle or a birdbath goes in a hard frost.
 *
 * The one cover that genuinely hides what is beneath it, which is why it is not the default — but it
 * is what a frozen pond in a north-facing garden actually looks like, and asking for it should not
 * mean forging a texture.
 */
const rimeIce: PaintFn = (u, v, out) => {
  const height = (x: number, y: number): number => fbm(x, y, 6, 3, 701) * 0.6 + fbm(x, y, 22, 2, 709) * 0.4;
  const h = height(u, v);
  const lit = clamp01(relief(height, u, v, 8) * 0.5 + h * 0.4 + 0.3);
  // Frost bloom: a fine crystalline sparkle, gated by where the frost actually took.
  //
  // Two attempts at literal *feathers* — stretched noise for the blades — are not in this file, and
  // the reason is worth keeping. Blades in one direction read as brushed fabric; blades in two
  // directions crosshatch into plaid whether they are added or blended between, because a tiling
  // swatch repeats the disagreement on a grid. Frost seen from standing height is granular anyway:
  // the ferns are a close-up phenomenon, and at 12 mm per texel there is nothing to draw.
  const bloom = smooth01((fbm(u, v, 4, 3, 731) - 0.44) / 0.28);
  const crystal = smooth01((fbm(u, v, 26, 3, 719) - 0.56) / 0.18);
  const feather = crystal * bloom;

  const dim = rgb(0.741, 0.788, 0.827);
  const bright = rgb(0.941, 0.961, 0.976);
  writeRgb(out, mixRgb(dim, bright, lit), feather * 0.06);
  out[3] = clamp01(0.78 + feather * 0.18 + h * 0.06);
};

/* ------------------------------------ The catalogue ------------------------------------ */

/** Typed as non-empty so the fallback in {@link snowSwatch} needs no cast to resolve. */
export const SNOW_SWATCHES: readonly [WinterSwatch<SnowSwatchId>, ...WinterSwatch<SnowSwatchId>[]] = [
  {
    id: 'fresh',
    kind: 'snow',
    name: 'Fresh powder',
    hint: 'An undisturbed overnight fall. Bright, soft-edged, blue in the hollows.',
    color: '#eef2f8',
    roughness: 0.94,
    tileMeters: 3,
    driftMeters: 15,
    character:
      'fresh unbroken snow lying 100–150 mm deep over everything, bright white with blue shadow in ' +
      'the hollows and soft rounded edges over every kerb and step',
    paint: freshPowder,
  },
  {
    id: 'settled',
    kind: 'snow',
    name: 'Settled snow',
    hint: 'A day or two old, wind-worked into shallow ridges. The everyday winter ground.',
    color: '#dde4ee',
    roughness: 0.88,
    tileMeters: 3.5,
    driftMeters: 17,
    character:
      'settled snow cover 60–100 mm deep, wind-worked into shallow ridges with a light crust, ' +
      'white going grey-blue in the shade',
    paint: settledSnow,
  },
  {
    id: 'trodden',
    kind: 'snow',
    name: 'Trodden snow',
    hint: 'Walked on and shovelled to the edges, going grey. A garden in use in January.',
    color: '#c4cbd3',
    roughness: 0.82,
    tileMeters: 2.5,
    driftMeters: 12,
    character:
      'trodden compacted snow, grey-white and lumpy with footfall, grit and leaf litter worked into ' +
      'it, thinning to bare ground along the edges of the paths',
    paint: troddenSnow,
  },
];

export const ICE_SWATCHES: readonly [WinterSwatch<IceSwatchId>, ...WinterSwatch<IceSwatchId>[]] = [
  {
    id: 'black',
    kind: 'ice',
    name: 'Clear ice',
    hint: 'A hard freeze straight onto the ground. Dark, glassy, the site showing through it.',
    color: '#5b6b74',
    roughness: 0.08,
    tileMeters: 4,
    driftMeters: 20,
    character:
      'a clear hard glaze of ice over the whole ground, dark and mirror-like with the paving and ' +
      'soil showing through it, pale pressure ridges and a bloom of frost on the surface',
    paint: blackIce,
  },
  {
    id: 'cracked',
    kind: 'ice',
    name: 'Plate ice',
    hint: 'Frozen, thawed and refrozen — a mosaic of plates with white fractures between them.',
    color: '#93a3ad',
    roughness: 0.14,
    tileMeters: 3.5,
    driftMeters: 18,
    character:
      'refrozen plate ice, a mosaic of clear plates with bright white fracture lines between them ' +
      'and trapped air below the surface',
    paint: crackedIce,
  },
  {
    id: 'rime',
    kind: 'ice',
    name: 'Rime frost',
    hint: 'An opaque milky freeze with frost feathers. Hides what is under it.',
    color: '#d3dde3',
    roughness: 0.55,
    tileMeters: 3,
    driftMeters: 15,
    character:
      'opaque milky rime ice with white frost feathers grown across it, hiding what it froze over',
    paint: rimeIce,
  },
];

export const DEFAULT_SNOW_SWATCH: SnowSwatchId = 'settled';
export const DEFAULT_ICE_SWATCH: IceSwatchId = 'black';

export function isSnowSwatchId(value: unknown): value is SnowSwatchId {
  return SNOW_SWATCHES.some((swatch) => swatch.id === value);
}

export function isIceSwatchId(value: unknown): value is IceSwatchId {
  return ICE_SWATCHES.some((swatch) => swatch.id === value);
}

/** The named snow swatch, falling back to the default rather than throwing on stored rubbish. */
export function snowSwatch(id: string): WinterSwatch {
  return (
    SNOW_SWATCHES.find((swatch) => swatch.id === id) ??
    SNOW_SWATCHES.find((swatch) => swatch.id === DEFAULT_SNOW_SWATCH) ??
    SNOW_SWATCHES[0]
  );
}

export function iceSwatch(id: string): WinterSwatch {
  return (
    ICE_SWATCHES.find((swatch) => swatch.id === id) ??
    ICE_SWATCHES.find((swatch) => swatch.id === DEFAULT_ICE_SWATCH) ??
    ICE_SWATCHES[0]
  );
}

/* ------------------------------------ Rasterisation ------------------------------------ */

/** Rendered swatches, keyed by `kind:id`. Drawn at most once per session per variant. */
const pixelCache = new Map<string, Uint8Array<ArrayBuffer>>();

/**
 * Draw a swatch to `WINTER_TEXELS²` RGBA bytes, ready to hand straight to a GPU texture.
 *
 * Raw bytes rather than a PNG data URL, unlike the palette's swatches: nothing needs to *store* one
 * of these — they are code, and regenerate identically — so the encode, the decode and the canvas
 * they would both need are all pure cost. It also means this works under test in node, where there
 * is no canvas at all.
 */
export function winterSwatchPixels(swatch: WinterSwatch): Uint8Array<ArrayBuffer> {
  const key = `${swatch.kind}:${swatch.id}`;
  const cached = pixelCache.get(key);
  if (cached) return cached;

  const n = WINTER_TEXELS;
  const data = new Uint8Array(n * n * 4);
  const out = [0, 0, 0, 0];
  for (let y = 0; y < n; y += 1) {
    const v = (y + 0.5) / n;
    for (let x = 0; x < n; x += 1) {
      const u = (x + 0.5) / n;
      swatch.paint(u, v, out);
      const i = (y * n + x) * 4;
      for (let c = 0; c < 4; c += 1) {
        data[i + c] = Math.max(0, Math.min(255, Math.round((out[c] ?? 0) * 255)));
      }
    }
  }

  pixelCache.set(key, data);
  return data;
}

/* -------------------------------------- Prose -------------------------------------- */

/**
 * One sentence describing the cover in force, for the capture manifest — or null when the ground is
 * bare. Without this a winter render comes back as a green lawn under a low sun, because the image
 * model is told the date and never told what is on the ground.
 */
export function describeGroundCover(cover: GroundCoverState): string | null {
  const snow = cover.snow ? snowSwatch(cover.snowSwatch) : null;
  const ice = cover.ice ? iceSwatch(cover.iceSwatch) : null;
  if (!snow && !ice) return null;

  const depth = (amount: number, text: string): string =>
    amount < 0.55 ? `a thin, broken cover of ${text}` : amount < 0.9 ? `an incomplete cover of ${text}` : text;

  const snowText = snow ? depth(cover.snowAmount, snow.character) : null;
  const iceText = ice ? depth(cover.iceAmount, ice.character) : null;
  if (snowText && iceText) return `${snowText}, lying in wind-scoured patches over ${iceText}`;
  return snowText ?? iceText;
}
